<!--
  ===========================================================================
  BINDING SECTION. Read before editing.

  The app repo has caught false privacy claims before shipping twice. The
  third: backup, which uploads the entire ledger to Google Drive or iCloud,
  got past a manual audit that found only two of the three places the claim
  was made. It is now pinned by a test, `privacy_claims_test.dart`, which
  fails on five absolutes and requires every claim to name `backup`.

  Nothing guards this repo. So the rule here is manual and absolute:

    State the rule, then name the exception in the same breath.

  Banned on this page, in any wording and in every language:
    "never leaves", "nothing syncs", "fully offline", "nowhere else",
    "no cloud", "no servers", "total privacy", "never shared with third
    parties", "your data never leaves your phone", "100% private",
    "completely offline", "zero data collection", or any claim that the app
    cannot reach the network.

  All of those were on this site before this rewrite. They are gone. If a
  headline is too short to carry the carve-out, the line immediately under it
  must carry it: that is the structure the app's own test assumes.
  ===========================================================================
-->
<section id="privacy" class="band relative isolate overflow-hidden py-[112px]">
  <div class="mx-auto max-w-[1180px] px-8">
    <div class="grid gap-16 lg:grid-cols-[1.1fr_1fr] lg:gap-24">
      <div>
        <p
          class="text-kicker uppercase text-accent-300 reveal"
          appReveal
          i18n="@@privacyKicker"
        >
          Where your data is
        </p>

        <!--
          `onboardingHeroTitle`, verbatim, including its line breaks. It is
          three lines and states the rule without the exception, which is the
          one exemption the app's test grants: on the condition that the line
          directly beneath carries the qualification. That line follows.
        -->
        <h2
          class="mt-kicker-gap text-headline text-neutral-100 reveal"
          appReveal
          i18n="@@privacyHeadline"
        >
          Your money data<br />stays on<br />this phone.
        </h2>

        <!--
          `onboardingHeroSubtitle`. This is the line that carries the carve-out.
          It must never be shortened, and the last clause must never be dropped
          to tighten the paragraph.

          As in the hero, "your device" became "your phone": read on a laptop,
          "your device" is the laptop, and the sentence stops being about the
          thing it is describing. The /privacy policy keeps "device", because
          there it is a legal statement that should cover a tablet too.
        -->
        <p
          class="mt-8 max-w-[52ch] text-body text-accent-200 reveal"
          appReveal
          i18n="@@privacySubtitle"
        >
          No account. No servers to breach. Ledgerly runs offline. The only copy
          that ever leaves your phone is a backup you switch on yourself.
        </p>

        <ul class="mt-[42px] space-y-6">
          @for (proof of proofs; track proof; let i = $index) {
          <li class="flex items-start gap-6 reveal" [appReveal]="i * 70">
            <svg
              class="mt-[3px] h-4 w-4 flex-none"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 8.5 6.2 12 13 4.5"
                stroke="var(--color-accent-300)"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="text-label text-neutral-100">{{ proof }}</span>
          </li>
          }
        </ul>

        <p class="mt-8 text-sub text-accent-300" i18n="@@privacyProofsNote">
          You can check all three yourself in about a minute, on a phone with
          no signal.
        </p>
      </div>

      <!--
        The honest half. Almost nobody publishes this list, which is why it
        belongs here. A privacy claim you have to take on faith is a slogan.
        One that names its own exceptions is evidence.

        This covers the APP only. The website's own analytics are on /privacy,
        because the website is not part of the product.
      -->
      <div class="reveal" appReveal [appReveal]="120">
        <div
          class="rounded-lg border border-neutral-100/15 bg-neutral-100/[0.06] p-8 backdrop-blur-sm"
        >
          <p class="text-kicker uppercase text-accent-300" i18n="@@privacyNetworkKicker">
            What does reach the network
          </p>

          <dl class="mt-8 space-y-8">
            @for (item of network; track item.title) {
            <div>
              <dt class="text-label text-neutral-100">{{ item.title }}</dt>
              <dd class="mt-2 text-sub leading-relaxed text-accent-200">
                {{ item.detail }}
              </dd>
            </div>
            }
          </dl>

          <div class="mt-8 h-px w-full bg-neutral-100/15"></div>

          <p
            class="mt-6 text-sub leading-relaxed text-accent-200"
            i18n="@@privacyLedgerNote"
          >
            Only backup carries what you have recorded, and it waits until you
            turn it on. The other two never see a figure.
          </p>

          <a
            routerLink="/privacy"
            class="mt-8 inline-block text-label text-accent-200 underline decoration-accent-300/40 underline-offset-4 hover:decoration-accent-300"
            i18n="@@privacyPolicyLink"
            >Read the privacy policy</a
          >
        </div>
      </div>
    </div>
  </div>
</section>
